import React from "react";
import { Builder, builder, withChildren } from "@builder.io/react";
import Link from "next/link";

import { analytics } from "@flare/analytics";
import type { ButtonProps as ButtonFlareUIProps } from "@flare/ui/components";
import { Button as ButtonFlareUI } from "@flare/ui/components";

import { BiEventsNames } from "@/services/analytics/bi-event-names";

import { navigationComponentsNames } from "../consts";
import type { BuilderAttributes, EventProps } from "../types";

type ButtonProps = ButtonFlareUIProps & {
  attributes?: BuilderAttributes;
  enablePreClientRedirect?: boolean;
  event?: EventProps;
  href?: string;
  loading?: boolean;
  pathname?: string;
  size: string;
  text?: string;
  variant: string;
};

// Placeholder is used to better explain users how to use the button in Builder platform.
const Placeholder = () => <p>Insert element inside</p>;

/**
 * For allowing backward compatibility with old design-system props,
 * we map the old props to the new ones.
 * once all builder pages are migrated to the new props,
 * we can remove this mappings.
 */
const legacyToCurrentVariantMap: Record<string, ButtonFlareUIProps["variant"]> =
  {
    square: "primary",
    unstyled: "primary",
    secondary: "primary",
  };
const legacyToCurrentSizeMap: Record<string, ButtonFlareUIProps["size"]> = {
  xl: "lg",
  xs: "sm",
};

export const Button = ({
  size,
  variant,
  children,
  event,
  href,
  pathname,
  onClick,
  attributes: builderAttributes,
  type = "submit",
  disabled = false,
  loading = false,
  text,
}: ButtonProps) => {
  /**
   * In cases when we'e using element binding data in Builder.io, during the page load before JS runs, the link is not set.
   * causing the Link to fallback to Next.js's default dynamic route pattern (e.g., /online/[practice]/[subPractice]/[stateName]).
   * In such cases, we need to provide a fallback URL.
   **/
  const buttonUrl = href || pathname || "/v2/funnel";
  const buttonContent = text || children;

  const localSize = legacyToCurrentSizeMap?.[size] || size;
  const localVariant = legacyToCurrentVariantMap?.[variant] || variant;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    analytics.track(BiEventsNames.WebPageCtaClick, {
      ...event?.eventData,
      click_url: buttonUrl,
      cta_text: buttonContent,
    });

    event?.name && builder.track(event?.name);
    onClick?.(e);
  };

  const { key, className, ...restBuilderAttributes } = builderAttributes || {};

  return (
    <ButtonFlareUI
      variant={localVariant}
      size={localSize}
      onClick={handleClick}
      type={type}
      disabled={disabled}
      isLoading={loading}
      key={key}
      className={className}
      {...restBuilderAttributes}
      asChild
    >
      <Link href={buttonUrl}>{buttonContent ?? <Placeholder />}</Link>
    </ButtonFlareUI>
  );
};

const ButtonWithChildren = withChildren(Button);

Builder.registerComponent(ButtonWithChildren, {
  name: navigationComponentsNames.BUTTON,
  noWrap: true,
  inputs: [
    { name: "href", type: "string" },
    { name: "pathname", type: "string" },
    {
      name: "variant",
      type: "string",
      defaultValue: "primary",
      enum: ["primary", "danger", "outline", "extra", "ghost"],
    },
    {
      name: "size",
      type: "string",
      defaultValue: "lg",
      enum: ["lg", "md", "sm"],
    },
    {
      name: "text",
      type: "string",
      defaultValue: "Enter CTA text",
      helperText:
        "Button text (can be overwritten by inserting child elements)",
    },
    {
      name: "type",
      type: "string",
      defaultValue: "submit",
      enum: ["submit", "button"],
      helperText: "use submit for forms and button for links",
    },
    {
      name: "event",
      type: "object",
      subFields: [
        {
          friendlyName: "Data",
          name: "eventData",
          type: "object",
          subFields: [
            {
              friendlyName: "Location in the page",
              name: "location",
              type: "string",
              helperText: "Examples: hero, fold_1.",
            },

            {
              friendlyName: "CTA text",
              name: "cta_text",
              type: "string",
            },
          ],
        },
      ],
    },
  ],
});
